import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useContext,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

export type NotificationType = 'error' | 'warning' | 'success';

export interface NotificationInfo {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
}

interface NotificationContextType {
  notifications: NotificationInfo[];
  showNotification: (
    notification: Omit<NotificationInfo, 'id'>,
  ) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider',
    );
  }
  return context;
};

const NotificationContext = createContext<
  NotificationContextType | undefined
>(undefined);

export const NotificationContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [notifications, setNotifications] = useState<
    NotificationInfo[]
  >([]);

  const showNotification = useCallback(
    (notification: Omit<NotificationInfo, 'id'>) => {
      const id = `notification-${uuidv4()}`;
      const newNotification: NotificationInfo = {
        ...notification,
        id,
      };

      setNotifications((prev) => [...prev, newNotification]);
      return id;
    },
    [],
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        removeNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export { NotificationContext };
export default NotificationContextProvider;
